<template>
  <div :id="cardId" class="flex flex-col gap-4">
    <div
      class="flex flex-col flex-1 gap-4"
      :class="{ 'cursor-pointer': props.isInfoClickable }"
      @click="$emit('comment-info-click', props.commentId)"
    >
      <div class="flex flex-row items-center gap-2">
        <review-avatar :username="reviewUsername" />
        <div class="flex flex-col gap-1 flex-1">
          <p v-if="reviewUsername" class="text-sm pl-0.5">{{ reviewUsername }}</p>
          <div class="flex flex-row">
            <svg-icon v-for="num in props.reviewRating" :key="num" name="star-filled" class="text-yellow-50 size-4" />
            <svg-icon v-for="num in MAX_RATING - props.reviewRating" :key="num" name="star-filled" class="text-gray-30 size-4" />
          </div>
        </div>
        <etg-button v-if="props.commentCount" type="text" size="mini" class="active self-start -mr-3" @click="$emit('comment-count-click')">
          {{ $t('booking_detail.comment.all', { review_count: formatNumberWithCommas(props.commentCount) }, props.commentCount) }}
        </etg-button>
      </div>
      <p v-if="props.reviewContent" class="whitespace-pre-wrap" :class="props.reviewContentClass">{{ props.reviewContent }}</p>
      <review-card-tags v-if="props.reviewTags.length" :tags="props.reviewTags" :one-line="props.oneLineTags" />
    </div>
    <div class="review-card-footer flex flex justify-between items-center gap-3">
      <p class="text-sm">{{ formatDateWithI18n(props.reviewCreateTime, 'MMMDYYYY') }}</p>
      <etg-button
        v-if="props.isUsefulBtnShown && props.commentId"
        :icon="likedIconName"
        :icon-color="likedIconColor"
        size="mini"
        class="!py-0 -mr-3"
        @click="onUsefulClick"
      >
        {{ $t('comment.useful.count', { count: formatNumberWithCommas(likesCount) }) }}
      </etg-button>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { HTMLAttributes } from 'vue'
import type { ReviewReviewTag } from '@/types/api'

import ReviewAvatar from '@/components/review/review-avatar.vue'
import ReviewCardTags from '@/components/review/review-card-tags.vue'

import { useAuthStore } from '@/store/auth'
import { REVIEW_CARD_ID_PREFIX } from '@/constants/commons'

interface Props {
  reviewUsername: string
  reviewRating: number
  reviewContent: string
  reviewTags: ReviewReviewTag[]
  reviewCreateTime: string
  isInfoClickable?: boolean
  commentId?: number
  commentCount?: number
  isUsefulBtnShown?: boolean
  isUseful?: boolean
  usefulCount?: number
  reviewContentClass?: HTMLAttributes['class']
  placement?: 'product' | 'order' | 'viewer'
  oneLineTags?: boolean
}

interface Emits {
  (event: 'comment-info-click', commentId?: number): void
  (event: 'comment-count-click'): void
}

const MAX_RATING = 5

const props = withDefaults(defineProps<Props>(), { isUsefulBtnShown: false, isUseful: false, usefulCount: 0, oneLineTags: false })
const { isUseful, usefulCount } = toRefs(props)

defineEmits<Emits>()

const authStore = useAuthStore()

const { formatDateWithI18n } = useI18nDateString()
const { likesCount, likedIconName, likedIconColor, fetchLikeReviewApi } = useLikeReview({ isUseful, usefulCount })

const cardId = computed(() => {
  return props.placement ? [props.placement, REVIEW_CARD_ID_PREFIX, props.commentId].join('-') : undefined
})

function onUsefulClick() {
  if (!authStore.isLogin) {
    authStore.addToLoginPendingActions(onUsefulClick)
    authStore.openLoginModal()

    return
  }

  fetchLikeReviewApi(props.commentId)
}
</script>
